import { useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import { useUserSites, useDeleteSite, useCreateSite, useUpdateSiteSettings } from '../hooks/useApi';

// Loading skeleton component
function SettingsSkeleton() {
  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="h-6 bg-gray-200 rounded animate-pulse mb-4"></div>
      <div className="space-y-4">
        <div className="border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="h-4 bg-gray-200 rounded animate-pulse mb-2"></div>
              <div className="h-3 bg-gray-200 rounded animate-pulse w-2/3"></div>
            </div>
            <div className="flex space-x-2">
              <div className="h-8 bg-gray-200 rounded animate-pulse w-12"></div>
              <div className="h-8 bg-gray-200 rounded animate-pulse w-16"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Error state component
function ErrorState({ error }: { error: Error }) {
  return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
      <div className="flex">
        <div className="flex-shrink-0">
          <div className="text-red-500 text-xl">⚠️</div>
        </div>
        <div className="ml-3">
          <h3 className="text-sm font-medium text-red-800">
            Failed to load settings
          </h3>
          <p className="mt-1 text-sm text-red-600">
            {error.message || 'An error occurred while fetching settings'}
          </p>
        </div>
      </div>
    </div>
  );
}

// Add site modal component
function AddSiteModal({ 
  isOpen, 
  onClose, 
  onConfirm, 
  isCreating 
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (domain: string, name?: string) => void;
  isCreating: boolean;
}) {
  const [domain, setDomain] = useState('');
  const [name, setName] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (domain.trim()) {
      onConfirm(domain.trim(), name.trim() || undefined);
    }
  };

  const handleClose = () => {
    setDomain('');
    setName('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Add New Site
        </h3>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="domain" className="block text-sm font-medium text-gray-700 mb-1">
              Domain *
            </label>
            <input
              type="text"
              id="domain"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="example.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
              disabled={isCreating}
            />
          </div>
          <div className="mb-6">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Site Name (optional)
            </label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My Awesome Site"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={isCreating}
            />
          </div>
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={isCreating}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isCreating || !domain.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {isCreating ? 'Creating...' : 'Create Site'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Delete confirmation modal component
function DeleteConfirmationModal({ 
  isOpen, 
  onClose, 
  onConfirm, 
  siteName,
  isDeleting 
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  siteName: string;
  isDeleting: boolean;
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md mx-4">
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Delete Site
        </h3>
        <p className="text-sm text-gray-600 mb-6">
          Are you sure you want to delete &ldquo;{siteName}&rdquo;? This action cannot be undone and will permanently remove all feedback data.
        </p>
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function SettingsPage() {
  const { data: sites, isLoading, error } = useUserSites();
  const deleteSiteMutation = useDeleteSite();
  const createSiteMutation = useCreateSite();
  const updateSettingsMutation = useUpdateSiteSettings();
  
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    siteId: string;
    siteName: string;
  }>({
    isOpen: false,
    siteId: '',
    siteName: '',
  });

  const [addSiteModal, setAddSiteModal] = useState(false);
  
  // Check if user has any paid sites
  const hasPaidSites = sites?.some(site => site.plan === 'pro' || site.plan === 'enterprise') || false;
  
  const [siteSettings, setSiteSettings] = useState<Record<string, {
    allowComments: boolean;
    showVoteCounts: boolean;
    theme: 'light' | 'dark';
  }>>({});

  const navigate = useNavigate();

  const handleDeleteSite = async () => {
    try {
      await deleteSiteMutation.mutateAsync({
        siteId: deleteModal.siteId,
        confirmDeletion: true,
      });
      
      toast.success('Site deleted successfully');
      setDeleteModal({ isOpen: false, siteId: '', siteName: '' });
    } catch (error) {
      console.error('Failed to delete site:', error);
      toast.error('Failed to delete site. Please try again.');
    }
  };

  const handleCreateSite = async (domain: string, name?: string) => {
    try {
      const result = await createSiteMutation.mutateAsync({
        domain,
        name,
      });
      
      toast.success(result.message);
      setAddSiteModal(false);
    } catch (error: unknown) {
      console.error('Failed to create site:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to create site. Please try again.';
      toast.error(errorMessage);
    }
  };

  const handleSaveConfiguration = async (siteId: string) => {
    try {
      const settings = siteSettings[siteId];
      if (!settings) {
        toast.error('No settings to save');
        return;
      }

      await updateSettingsMutation.mutateAsync({
        siteId,
        settings: {
          allowComments: settings.allowComments,
          showVoteCounts: settings.showVoteCounts,
          theme: settings.theme,
        },
      });
      
      toast.success('Site configuration saved successfully');
    } catch (error: unknown) {
      console.error('Failed to save configuration:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to save configuration. Please try again.';
      toast.error(errorMessage);
    }
  };

  const updateSiteSetting = (siteId: string, key: string, value: boolean | string) => {
    setSiteSettings(prev => ({
      ...prev,
      [siteId]: {
        ...prev[siteId],
        [key]: value,
      },
    }));
  };

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage your sites and widget configuration
          </p>
        </div>
        <ErrorState error={error as Error} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="mt-1 text-sm text-gray-600">
          Manage your sites and widget configuration
        </p>
      </div>
      
      {isLoading ? (
        <SettingsSkeleton />
      ) : (
        <>
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              Your Sites
            </h2>
            <div className="space-y-4">
              {sites && sites.length > 0 ? (
                sites.map((site) => (
                  <div key={site.id} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-medium text-gray-900">
                          {site.domain}
                        </h3>
                        <p className="text-sm text-gray-500">
                          Site ID: {site.id}
                        </p>
                        <div className="flex items-center space-x-4 mt-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            site.plan === 'pro' 
                              ? 'bg-indigo-100 text-indigo-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {site.plan === 'pro' ? 'Pro Plan' : 'Free Plan'}
                          </span>
                          <span className="text-xs text-gray-500">
                            {site.totalVotes.toLocaleString()} total votes
                          </span>
                          <span className="text-xs text-gray-500">
                            {site.monthlyCount.toLocaleString()} this month
                          </span>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button className="text-indigo-600 hover:text-indigo-500 text-sm">
                          Edit
                        </button>
                        <button 
                          onClick={() => setDeleteModal({
                            isOpen: true,
                            siteId: site.id,
                            siteName: site.domain,
                          })}
                          className="text-red-600 hover:text-red-500 text-sm"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <div className="text-gray-400 text-4xl mb-4">🌐</div>
                  <p className="text-gray-500">No sites configured yet</p>
                  <p className="text-sm text-gray-400 mt-1">
                    Add your first site to start collecting feedback
                  </p>
                </div>
              )}
            </div>
            {hasPaidSites ? (
              <button 
                onClick={() => setAddSiteModal(true)}
                className="mt-4 bg-indigo-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-indigo-700"
              >
                Add New Site
              </button>
            ) : (
              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">
                      Upgrade to Pro Required
                    </h3>
                    <div className="mt-1 text-sm text-yellow-700">
                      <p>
                        Adding additional sites requires a Pro or Enterprise plan. 
                        <a href="/dashboard/billing" className="font-medium underline hover:text-yellow-600">
                          Upgrade your plan
                        </a> to add more sites.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              Widget Installation
            </h2>
            <p className="text-sm text-gray-600 mb-6">
              Choose your preferred widget style and add the code to your website:
            </p>
            
            {/* Widget Style Options */}
            <div className="space-y-6 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Inline Widget */}
                <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900">Inline Widget (Default)</h4>
                      <p className="text-xs text-gray-600 mt-1">
                        Embeds directly into your page content. Perfect for articles, sidebars, or anywhere you want immediate feedback.
                      </p>
                    </div>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      Default
                    </span>
                  </div>
                  <div className="text-xs text-gray-500">
                    Classic thumbs up/down interface with optional comments
                  </div>
                </div>

                {/* Drawer Widget */}
                <div className="border rounded-lg p-4 bg-purple-50 border-purple-200">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900">Drawer Widget (New)</h4>
                      <p className="text-xs text-gray-600 mt-1">
                        Creates a fixed &ldquo;Feedback&rdquo; button on the right edge that opens a modal. Non-intrusive and great for any page.
                      </p>
                    </div>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      New
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mb-2">
                    Sleek drawer-style with same thumbs up/down functionality
                  </div>
                  <a 
                    href="/demo" 
                    rel="noopener noreferrer"
                    className="text-xs text-purple-600 hover:text-purple-800 font-medium"
                  >
                    View Demo →
                  </a>
                </div>
              </div>
            </div>

            {sites && sites.length > 0 ? (
              <div className="space-y-6">
                {sites.map((site) => (
                  <div key={site.id} className="border rounded-lg p-4">
                    <h3 className="text-sm font-medium text-gray-900 mb-4">
                      {site.domain}
                    </h3>
                    
                    {/* Inline Widget Code */}
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-medium text-gray-700">Inline Widget</h4>
                      </div>
                      <div className="bg-gray-100 rounded-md p-3">
                        <code className="text-sm text-gray-800">
                          {`<script src="https://microfb.com/widget/widget.js"></script>
<div data-mfb-site-id="${site.id}" data-mfb-skin="inline" data-mfb-theme="light"></div>`}
                        </code>
                      </div>
                      <button 
                        onClick={() => {
                          navigator.clipboard.writeText(
                            `<script src="https://microfb.com/widget/widget.js"></script>\n<div data-mfb-site-id="${site.id}" data-mfb-skin="inline" data-mfb-theme="light"></div>`
                          );
                          toast.success('Inline widget code copied to clipboard');
                        }}
                        className="mt-2 text-sm text-indigo-600 hover:text-indigo-500"
                      >
                        Copy inline widget code
                      </button>
                    </div>

                    {/* Drawer Widget Code */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-medium text-gray-700">Drawer Widget</h4>
                        <span className="text-xs text-purple-600 font-medium">New!</span>
                      </div>
                      <div className="bg-gray-100 rounded-md p-3">
                        <code className="text-sm text-gray-800">
                          {`<script src="https://microfb.com/widget/widget.js"></script>
<div data-mfb-site-id="${site.id}" data-mfb-skin="drawer" data-mfb-theme="light"></div>`}
                        </code>
                      </div>
                      <button 
                        onClick={() => {
                          navigator.clipboard.writeText(
                            `<script src="https://microfb.com/widget/widget.js"></script>\n<div data-mfb-site-id="${site.id}" data-mfb-skin="drawer" data-mfb-theme="light"></div>`
                          );
                          toast.success('Drawer widget code copied to clipboard');
                        }}
                        className="mt-2 text-sm text-indigo-600 hover:text-indigo-500"
                      >
                        Copy drawer widget code
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-gray-100 rounded-md p-4">
                  <div className="mb-3">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Inline Widget</h4>
                    <code className="text-sm text-gray-800">
                      {`<script src="https://microfb.com/widget/widget.js"></script>
<div data-mfb-site-id="your-site-id" data-mfb-skin="inline" data-mfb-theme="light"></div>`}
                    </code>
                  </div>
                </div>
                <div className="bg-gray-100 rounded-md p-4">
                  <div className="mb-3">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Drawer Widget</h4>
                    <code className="text-sm text-gray-800">
                      {`<script src="https://microfb.com/widget/widget.js"></script>
<div data-mfb-site-id="your-site-id" data-mfb-skin="drawer" data-mfb-theme="light"></div>`}
                    </code>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {sites && sites.length > 0 && (
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                Widget Configuration
              </h2>
              <div className="space-y-6">
                {sites.map((site) => (
                  <div key={site.id} className="border rounded-lg p-4">
                    <h3 className="text-sm font-medium text-gray-900 mb-3">
                      {site.domain}
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={siteSettings[site.id]?.allowComments ?? site.allowComments}
                            onChange={(e) => updateSiteSetting(site.id, 'allowComments', e.target.checked)}
                            className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                          />
                          <span className="ml-2 text-sm text-gray-700">
                            Allow comments
                          </span>
                        </label>
                      </div>
                      <div>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={siteSettings[site.id]?.showVoteCounts ?? site.showVoteCounts}
                            onChange={(e) => updateSiteSetting(site.id, 'showVoteCounts', e.target.checked)}
                            className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                          />
                          <span className="ml-2 text-sm text-gray-700">
                            Show vote counts
                          </span>
                        </label>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Widget Theme
                        </label>
                        <div className="flex space-x-4">
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name={`theme-${site.id}`}
                              value="light"
                              checked={(siteSettings[site.id]?.theme ?? 'light') === 'light'}
                              onChange={(e) => updateSiteSetting(site.id, 'theme', e.target.value)}
                              className="border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                            />
                            <span className="ml-2 text-sm text-gray-700">Light</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name={`theme-${site.id}`}
                              value="dark"
                              checked={(siteSettings[site.id]?.theme ?? 'light') === 'dark'}
                              onChange={(e) => updateSiteSetting(site.id, 'theme', e.target.value)}
                              className="border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                            />
                            <span className="ml-2 text-sm text-gray-700">Dark</span>
                          </label>
                        </div>
                      </div>
                      <button 
                        onClick={() => handleSaveConfiguration(site.id)}
                        disabled={updateSettingsMutation.isPending}
                        className="bg-indigo-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
                      >
                        {updateSettingsMutation.isPending ? 'Saving...' : 'Save Configuration'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Account Deletion Section */}
          <div className="bg-white shadow rounded-lg p-6 border border-red-200">
            <h2 className="text-lg font-medium text-red-900 mb-4">
              ⚠️ Danger Zone
            </h2>
            <div className="space-y-4">
              <div className="bg-red-50 rounded-md p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      Delete Your Account
                    </h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p>
                        This will permanently delete your account and all associated data including:
                      </p>
                      <ul className="mt-1 list-disc list-inside">
                        <li>All sites and their feedback data</li>
                        <li>Analytics and statistics</li>
                        <li>Payment history and subscriptions</li>
                        <li>API keys and webhooks</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-600">
                  This action cannot be undone and complies with GDPR data deletion requirements.
                </p>
                <button
                  onClick={() => navigate('/account/delete')}
                  className="bg-red-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                >
                  Delete Account
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      <AddSiteModal
        isOpen={addSiteModal}
        onClose={() => setAddSiteModal(false)}
        onConfirm={handleCreateSite}
        isCreating={createSiteMutation.isPending}
      />

      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, siteId: '', siteName: '' })}
        onConfirm={handleDeleteSite}
        siteName={deleteModal.siteName}
        isDeleting={deleteSiteMutation.isPending}
      />
    </div>
  );
} 